import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Loader2 } from "lucide-react";
import { payoutPeriodLabel } from "@/lib/payout-period";
import { api, type DownloadedFile } from "@/api";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { formatPrice } from "@/lib/utils";
import { useAuth } from "@/store";
const saveFile = (file: DownloadedFile) => {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** Сегодня и первое число месяца — по Москве, как считает шлюз. */
const mskToday = (): string => new Date(Date.now() + 3 * 3_600_000).toISOString().slice(0, 10);
const month = payoutPeriodLabel;

/**
 * Реестр платёжных поручений по одобренным заявкам (встреча 25.09.2026).
 *
 * Бухгалтер выгружает файл обмена для 1С (1CClientBankExchange) и загружает
 * его в «Бухгалтерию», откуда платёжки уходят в банк. Строка без реквизитов
 * продавца видна здесь с пометкой, что именно не заполнено, и в файл для банка
 * не попадает. Реквизиты площадки задаются в окружении шлюза — без них файл не
 * собирается, и карточка прямо говорит, каких переменных не хватает.
 */
function PaymentRegistryCard({
  canExport
}: {
  canExport: boolean;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<"1c" | "xlsx" | null>(null);
  const [message, setMessage] = useState<{
    tone: "ok" | "error";
    text: string;
  } | null>(null);
  const registry = useQuery({
    queryKey: ["payouts", "registry"],
    queryFn: () => api.finances.payoutRegistry()
  });
  const download = async (format: "1c" | "xlsx") => {
    setBusy(format);
    setMessage(null);
    try {
      const file = await api.finances.exportPayoutRegistry(format);
      saveFile(file);
      setMessage({
        tone: "ok",
        text: format === "1c" ? `Файл для 1С: платёжек ${file.rows}${file.skipped ? `, пропущено без реквизитов ${file.skipped}` : ""}.` : `Реестр выгружен: строк ${file.rows}.`
      });
      // Выгрузка для 1С выдаёт заявкам номера платёжек — обновляем очередь.
      await queryClient.invalidateQueries({
        queryKey: ["payouts"]
      });
    } catch (e) {
      setMessage({
        tone: "error",
        text: e instanceof Error ? e.message : String(e)
      });
    } finally {
      setBusy(null);
    }
  };
  const data = registry.data;
  const readyCount = data?.lines.filter(l => l.ready).length ?? 0;
  return <Card data-tour="finances-registry">
			<CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
				<div>
					<CardTitle>Реестр платёжных поручений</CardTitle>
					<p className="mt-1 text-sm text-muted-foreground">
						Одобренные заявки на выплату. Файл для 1С — формат обмена с банк-клиентом
						(1CClientBankExchange, Windows-1251).
					</p>
				</div>
				{canExport && <div className="flex flex-wrap gap-2">
						<Button size="sm" disabled={busy !== null || readyCount === 0 || !data?.payer} onClick={() => void download("1c")}>
							{busy === "1c" ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}
							Файл для 1С (.txt)
						</Button>
						<Button size="sm" variant="outline" disabled={busy !== null || (data?.lines.length ?? 0) === 0} onClick={() => void download("xlsx")}>
							{busy === "xlsx" ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
							Реестр (.xlsx)
						</Button>
					</div>}
			</CardHeader>
			<CardContent className="flex flex-col gap-3">
				{data && !data.payer && <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300">
						Не заданы реквизиты площадки-плательщика: {data.missingPayerEnv.join(", ")}. Их задают в
						окружении шлюза (.env на сервере). Пока их нет, файл для 1С не собирается — xlsx-реестр
						выгрузить можно.
					</p>}
				{message && <p className={message.tone === "ok" ? "text-sm text-emerald-700 dark:text-emerald-400" : "text-sm text-destructive"}>
						{message.text}
					</p>}
				{registry.isPending ? <p className="text-sm text-muted-foreground">Загрузка…</p> : registry.isError ? <p className="text-sm text-destructive">{registry.error.message}</p> : (data?.lines.length ?? 0) === 0 ? <p className="text-sm text-muted-foreground">Одобренных заявок нет — выгружать нечего.</p> : <>
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Получатель</TableHead>
										<TableHead>ИНН</TableHead>
										<TableHead>Счёт / БИК</TableHead>
										<TableHead>Период</TableHead>
										<TableHead className="text-right">Сумма</TableHead>
										<TableHead>№ п/п</TableHead>
										<TableHead>Реквизиты</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data?.lines.map(line => <TableRow key={line.request.id} className={line.ready ? undefined : "bg-destructive/5"}>
											<TableCell>
												<div className="font-medium">{line.recipient.name || "—"}</div>
												<div className="text-xs text-muted-foreground">
													{line.request.shopName ?? line.request.shopId}
												</div>
											</TableCell>
											<TableCell className="font-mono text-xs">{line.recipient.inn || "—"}</TableCell>
											<TableCell className="font-mono text-xs">
												{line.recipient.account || "—"}
												<div className="text-muted-foreground">
													{line.recipient.bik || "—"} · {line.recipient.bankName || "банк не указан"}
												</div>
											</TableCell>
											<TableCell className="text-sm">{month(line.request.period)}</TableCell>
											<TableCell className="text-right font-semibold tabular-nums">
												{formatPrice(line.request.amount)}
											</TableCell>
											<TableCell className="tabular-nums">{line.request.paymentOrderNumber ?? "—"}</TableCell>
											<TableCell>
												{line.ready ? <Badge variant="secondary">Готово</Badge> : <div className="flex flex-col gap-1">
														<Badge variant="destructive">Не пойдёт в файл</Badge>
														<span className="text-xs text-destructive">Нет: {line.problems.join(", ")}</span>
													</div>}
											</TableCell>
										</TableRow>)}
								</TableBody>
							</Table>
						</div>
						<p className="text-sm text-muted-foreground">
							Всего {formatPrice(data?.total ?? 0)} · к выгрузке в 1С {formatPrice(data?.readyTotal ?? 0)} (
							{readyCount} из {data?.lines.length ?? 0})
						</p>
					</>}
			</CardContent>
		</Card>;
}
const SECTION_LABEL: Record<string, string> = {
  receipts: "Поступления от покупателей",
  refunds: "Возвраты покупателям",
  payouts: "Выплаты продавцам",
  commission: "Комиссия площадки (справочно)"
};

/**
 * «Движение денежных средств» за период. Раньше из «Финансов» выгружалась
 * карточка товаров — заказчику нужна была именно финансовая выгрузка:
 * поступления, возвраты, комиссия и выплаты продавцам из реальных таблиц.
 */
function CashFlowCard({
  canExport
}: {
  canExport: boolean;
}) {
  const [from, setFrom] = useState(`${mskToday().slice(0, 7)}-01`);
  const [to, setTo] = useState(mskToday());
  const [busy, setBusy] = useState<"xlsx" | "csv" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  const report = useQuery({
    queryKey: ["finances", "cash-flow", from, to],
    queryFn: () => api.finances.cashFlow(from, to),
    enabled: valid
  });
  const download = async (format: "xlsx" | "csv") => {
    setBusy(format);
    setError(null);
    try {
      saveFile(await api.finances.exportCashFlow(from, to, format));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  const totals = report.data?.totals;
  const lines: [string, number, string?][] = totals ? [[SECTION_LABEL.receipts ?? "", totals.receipts], [SECTION_LABEL.refunds ?? "", -totals.refunds], [SECTION_LABEL.payouts ?? "", -totals.payouts], ["Сальдо за период", totals.net, "font-semibold"], [SECTION_LABEL.commission ?? "", totals.commission, "text-muted-foreground"]] : [];
  return <Card data-tour="finances-cashflow">
			<CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
				<div>
					<CardTitle>Движение денежных средств (ДДС)</CardTitle>
					<p className="mt-1 text-sm text-muted-foreground">
						Поступления от покупателей, возвраты, комиссия и выплаты продавцам за период — из заказов,
						возвратов и проведённых заявок.
					</p>
				</div>
				{canExport && <div className="flex flex-wrap gap-2">
						<Button size="sm" disabled={!valid || busy !== null} onClick={() => void download("xlsx")}>
							{busy === "xlsx" ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
							Выгрузить ДДС (.xlsx)
						</Button>
						<Button size="sm" variant="outline" disabled={!valid || busy !== null} onClick={() => void download("csv")}>
							CSV
						</Button>
					</div>}
			</CardHeader>
			<CardContent className="flex flex-col gap-3">
				<div className="flex flex-wrap items-end gap-3">
					<label className="flex flex-col gap-1 text-xs text-muted-foreground">
						С
						<Input type="date" value={from} max={to} onChange={e_0 => setFrom(e_0.target.value)} className="h-9 w-40" />
					</label>
					<label className="flex flex-col gap-1 text-xs text-muted-foreground">
						По
						<Input type="date" value={to} min={from} onChange={e_1 => setTo(e_1.target.value)} className="h-9 w-40" />
					</label>
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				{!valid ? <p className="text-sm text-destructive">Начало периода позже конца.</p> : report.isPending ? <p className="text-sm text-muted-foreground">Считаю…</p> : report.isError ? <p className="text-sm text-destructive">{report.error.message}</p> : <dl className="grid max-w-xl grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm">
						{lines.map(([label, value, cls]) => <div key={label} className={`contents ${cls ?? ""}`}>
								<dt className={cls?.includes("muted") ? "text-muted-foreground" : ""}>{label}</dt>
								<dd className="text-right tabular-nums">
									{value < 0 ? `− ${formatPrice(-value)}` : formatPrice(Math.abs(value))}
								</dd>
							</div>)}
						<p className="col-span-2 pt-1 text-xs text-muted-foreground">
							Операций: {report.data?.rows.length ?? 0}. Комиссия удерживается внутри поступления и в
							сальдо не входит.
						</p>
					</dl>}
			</CardContent>
		</Card>;
}

/** Выгрузки для бухгалтерии: реестр платёжек для 1С и ДДС. */
export function AccountingExports() {
  const $ = _c(11);
  const user = useAuth(_temp);
  let t0;
  if ($[0] !== user) {
    t0 = hasAdminPermission(user, "action.payouts.manage") || hasAdminPermission(user, "action.finances.manage");
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canPay = t0;
  let t1;
  if ($[2] !== canPay) {
    t1 = <PaymentRegistryCard canExport={canPay} />;
    $[2] = canPay;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== user) {
    t2 = hasAdminPermission(user, "action.exports.run");
    $[4] = user;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <CashFlowCard canExport={t2} />;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <div className="flex flex-col gap-4" data-tour="finances-accounting">{t1}{t3}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function _temp(st) {
  return st.user;
}